import React, { useCallback, useEffect, useState } from 'react';
import Badge from './ui/Badge';
import Modal from './ui/Modal';
import Spinner from './ui/Spinner';
import { adminApi } from '../config/axios';
import { useGlobal } from '../contexts/GlobalContext';
import { dateTimeFormat } from '../config/utils';
import {
  DELAY_KEY, DELAY_MAX, DELAY_MIN, FLAG_META, VALUE_LABEL, VALUE_VARIANT,
  apiErrorMessage, auditValue, buildRows, flagName, sourceLabel, validateDelay,
} from '../utils/protections';

/**
 * Protection flags + HDMI-IN audio delay for ONE scope.
 *  scope="device": effective values (with the source that supplied them) for one device,
 *                  set/clear a DEVICE override.            -> GET /admin/protections/device/{id}
 *  scope="global": fleet defaults, set/clear a GLOBAL override.   -> GET /admin/protections
 * Writes: PUT/DELETE /admin/protections. Audit: GET /admin/protections/audit.
 */
export default function ProtectionsPanel({ scope, scopeId }) {
  const { showToast } = useGlobal();
  const [rows, setRows]       = useState(null);
  const [loadErr, setLoadErr] = useState(null);
  const [audit, setAudit]     = useState([]);
  const [auditOpen, setAuditOpen] = useState(false);
  const [busyKey, setBusyKey] = useState(null);
  const [confirmOff, setConfirmOff] = useState(null);   // row awaiting "Turn off" confirmation
  const [drafts, setDrafts]   = useState({});           // delay input text by key

  const load = useCallback(async () => {
    setLoadErr(null);
    try {
      const [main, log] = await Promise.all([
        scope === 'device' ? adminApi.get(`/protections/device/${scopeId}`) : adminApi.get('/protections'),
        adminApi.get('/protections/audit', { params: { scope, ...(scope === 'device' ? { scope_id: scopeId } : {}), per_page: 10 } }),
      ]);
      const built = buildRows(scope, main.data);
      setRows(built);
      setAudit(log.data?.data ?? []);
      setDrafts(Object.fromEntries(built.filter(r => r.kind === 'delay').map(r => [r.key, String(r.value)])));
    } catch (e) {
      setLoadErr(apiErrorMessage(e, 'Could not load protections.'));
    }
  }, [scope, scopeId]);

  useEffect(() => { setRows(null); load(); }, [load]);

  const body = (flag) => ({ scope, ...(scope === 'device' ? { scope_id: scopeId } : {}), flag });

  const write = async (row, value) => {
    setBusyKey(row.key);
    try {
      const res = await adminApi.put('/protections', { ...body(row.key), value });
      const pushed = Number(res?.data?.push?.sent) || 0;   // build 413: the device(s) were told to fetch now (the heartbeat pull is the fallback)
      showToast('success', 'Saved', `${flagName(row.key)} set to ${auditValue(row.key, String(value))} (${scope} override).${pushed > 0 ? ` ${pushed === 1 ? 'The device was' : `${pushed} devices were`} told to apply it now.` : ''}`);
      await load();
    } catch (e) {
      showToast('error', 'Not saved', apiErrorMessage(e));
    } finally { setBusyKey(null); }
  };

  const reset = async (row) => {
    setBusyKey(row.key);
    try {
      await adminApi.delete('/protections', { data: body(row.key) });
      showToast('success', 'Reset', `${flagName(row.key)} now inherits.`);
      await load();
    } catch (e) {
      showToast('error', 'Not reset', apiErrorMessage(e));
    } finally { setBusyKey(null); }
  };

  const choose = (row, value) => {
    if (value === 'off') setConfirmOff(row);
    else write(row, value);
  };

  if (loadErr) {
    return (
      <div className="flex items-center justify-between gap-4">
        <p className="text-sm text-danger">{loadErr}</p>
        <button className="btn-secondary text-xs" onClick={() => { setRows(null); load(); }}>Retry</button>
      </div>
    );
  }
  if (!rows) return <div className="flex justify-center py-6"><Spinner /></div>;

  return (
    <div>
      <div className="divide-y divide-white/[0.05]">
        {rows.map(row => {
          const busy = busyKey === row.key;
          const meta = FLAG_META[row.key];
          return (
            <div key={row.key} className="py-3 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-text-main">{flagName(row.key)}</p>
                {meta && <p className="text-xs text-text-muted mt-0.5">{meta.desc}</p>}
                <div className="flex items-center gap-2 mt-1.5">
                  {row.kind === 'flag'
                    ? <Badge variant={VALUE_VARIANT[row.value] ?? 'muted'}>{VALUE_LABEL[row.value] ?? row.value}</Badge>
                    : <Badge variant="accent">{row.value} ms</Badge>}
                  <span className="text-xs text-text-muted">{sourceLabel(row.source, scope)}</span>
                  {row.kind === 'delay' && <span className="text-xs text-text-muted">· Default: {row.def} ms</span>}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-shrink-0">
                {row.kind === 'flag' ? (
                  ['on', 'observe_only', 'off'].map(v => (
                    <button
                      key={v}
                      disabled={busy}
                      onClick={() => choose(row, v)}
                      className={`text-xs ${row.overridden && row.value === v ? 'btn-primary' : v === 'off' ? 'btn-danger' : 'btn-secondary'}`}
                    >{VALUE_LABEL[v]}</button>
                  ))
                ) : (
                  <DelayControl
                    row={row}
                    busy={busy}
                    draft={drafts[row.key] ?? ''}
                    onDraft={t => setDrafts(d => ({ ...d, [row.key]: t }))}
                    onSave={() => write(row, Number(drafts[row.key]))}
                  />
                )}
                {row.overridden && (
                  <button disabled={busy} onClick={() => reset(row)} className="btn-secondary text-xs">Reset to inherited</button>
                )}
                {busy && <Spinner size="sm" />}
              </div>
            </div>
          );
        })}
      </div>

      {/* Recent changes */}
      <div className="mt-4 border-t border-white/[0.05] pt-3">
        <button className="text-xs text-text-muted hover:text-text-main" onClick={() => setAuditOpen(o => !o)}>
          {auditOpen ? '▾' : '▸'} Recent changes ({audit.length})
        </button>
        {auditOpen && (
          audit.length === 0
            ? <p className="text-xs text-text-muted mt-2">No changes recorded.</p>
            : (
              <div className="mt-2 overflow-x-auto">
                <table className="w-full">
                  <thead><tr>
                    <th className="table-header">Time</th><th className="table-header">Item</th>
                    <th className="table-header">Change</th><th className="table-header">Action</th><th className="table-header">By</th>
                  </tr></thead>
                  <tbody>
                    {audit.map(a => (
                      <tr key={a.id} className="table-row">
                        <td className="table-cell text-xs whitespace-nowrap">{dateTimeFormat(a.created_at)}</td>
                        <td className="table-cell text-xs">{flagName(a.flag)}</td>
                        <td className="table-cell text-xs">{auditValue(a.flag, a.old_value)} → {auditValue(a.flag, a.new_value)}</td>
                        <td className="table-cell text-xs"><Badge variant={a.action === 'clear' ? 'muted' : 'accent'}>{a.action}</Badge></td>
                        <td className="table-cell text-xs">{a.changed_by_email ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
        )}
      </div>

      <Modal
        open={!!confirmOff}
        onClose={() => setConfirmOff(null)}
        title="Confirm"
        size="sm"
        footer={
          <>
            <button className="btn-secondary" onClick={() => setConfirmOff(null)}>Cancel</button>
            <button className="btn-danger" onClick={() => { const r = confirmOff; setConfirmOff(null); write(r, 'off'); }}>Turn off</button>
          </>
        }
      >
        <p className="text-text-muted text-sm">
          {confirmOff && `Turn off ${flagName(confirmOff.key)} for ${scope === 'device' ? 'this device' : 'every device'}?`}
        </p>
      </Modal>
    </div>
  );
}

function DelayControl({ row, busy, draft, onDraft, onSave }) {
  const err = validateDelay(draft);
  return (
    <div className="flex flex-col items-end gap-1">
      <div className="flex items-center gap-2">
        <input
          type="number" inputMode="numeric" step="1" min={DELAY_MIN} max={DELAY_MAX}
          value={draft}
          onChange={e => onDraft(e.target.value)}
          className="input-base w-24 text-sm"
          aria-label="Audio delay in milliseconds"
        />
        <span className="text-xs text-text-muted">ms</span>
        <button className="btn-primary text-xs" disabled={busy || !!err} onClick={onSave}>Save</button>
      </div>
      {draft !== '' && err && <p className="text-xs text-danger">{err}</p>}
    </div>
  );
}
